import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Brain, Layers, Zap, Target, Shield, Network, GitBranch } from 'lucide-react';
import './AgentProfile.css';

// Export agents data for use in Header
export const agents = {
  'alphazero-andy': {
    name: "AlphaZero Andy",
    type: "alphazero",
    iconUrl: "https://fqodzpifyovgmqjlluin.supabase.co/storage/v1/object/public/avatars/alphazero_andy_0.png",
    mctsRollouts: 10,
    totalParameters: 34188,
    architecture: {
      gameSize: "3E",
      numMoves: "3E",
      inputChannels: 1,
      convChannels: 16,
      resBlocks: 2,
      policyLayers: [144, 32, 9],
      valueLayers: [144, 32, 64, 1]
    },
    description: "A swift and calculated opponent, Andy excels at reading the board with surgical precision. His compact neural architecture allows for rapid decision-making without sacrificing strategic depth.",
    strengths: ["Fast computation", "Balanced play", "Adaptive strategy"],
    difficulty: "Intermediate"
  },
  // 'alphazero-amara': {
  //   name: "AlphaZero Amara",
  //   type: "alphazero",
  //   iconUrl: "https://fqodzpifyovgmqjlluin.supabase.co/storage/v1/object/public/avatars/alphazero_amara_0.png",
  //   mctsRollouts: 100,
  //   totalParameters: 223437,
  //   architecture: {
  //     gameSize: "3E",
  //     numMoves: "3E",
  //     inputChannels: 1,
  //     convChannels: 32,
  //     resBlocks: 5,
  //     policyLayers: [288, 64, 9],
  //     valueLayers: [288, 64, 128, 1]
  //   },
  //   description: "Amara brings enhanced tactical awareness with her deeper network. With more residual blocks and wider layers, she can spot intricate patterns that shallower networks miss. Her increased rollouts make her a formidable mid-tier opponent.",
  //   strengths: ["Pattern recognition", "Deep analysis", "Tactical precision"],
  //   difficulty: "Advanced"
  // },
  'alphaq-up': {
    name: "AlphaQ Up",
    type: "other",
    iconUrl: "https://fqodzpifyovgmqjlluin.supabase.co/storage/v1/object/public/avatars/alphaq_up_0.png",
    mctsRollouts: 1000,
    totalParameters: 6056015,
    architecture: {
      gameSize: "3E",
      numMoves: "3E",
      inputChannels: 1,
      convChannels: 128,
      resBlocks: 9,
      policyLayers: [1152, 256, 9],
      valueLayers: [1152, 256, 128, 1]
    },
    description: "The apex predator. Most likely playing optimally from every board position. Designed for players seeking the ultimate challenge.",
    strengths: ["Invincible... maybe"],
    difficulty: "Master"
  },
  'mcts-murray': {
    name: "MCTS Murray",
    type: "mcts",
    iconUrl: "https://fqodzpifyovgmqjlluin.supabase.co/storage/v1/object/public/avatars/mcts_murray_0.png",
    mctsRollouts: 10,
    description: "Murray is a pure Monte Carlo Tree Search agent perfect for beginners. Without neural guidance, he relies on random playouts to evaluate positions. His 10 rollouts per move make him quick but occasionally unpredictable—a great starting opponent for learning the game.",
    strengths: ["Quick decisions", "Unpredictable style", "Beginner-friendly"],
    difficulty: "Beginner"
  },
  'mcts-melissa': {
    name: "MCTS Melissa",
    type: "mcts",
    iconUrl: "https://fqodzpifyovgmqjlluin.supabase.co/storage/v1/object/public/avatars/mcts_melissa_0.png",
    mctsRollouts: 1000,
    description: "Melissa harnesses the raw power of Monte Carlo Tree Search with an impressive 1000 rollouts per move. While lacking neural network intuition, her extensive simulation depth allows her to discover strong moves through brute-force exploration. A worthy challenge for intermediate players.",
    strengths: ["Extensive search", "Thorough analysis", "Reliable tactics"],
    difficulty: "Intermediate"
  }
};

const AgentProfile = () => {
  const { agentId } = useParams();

  // Select which agent to display based on URL parameter, default to Andy
  const agent = agents[agentId] || agents['alphazero-andy'];

  const [selectedTab, setSelectedTab] = useState('overview');

  const NetworkVisualization = () => {
    if (agent.type === 'mcts') {
      return (
        <div className="network-viz">
          <div className="mcts-explanation">
            <div className="mcts-header">
              <GitBranch className="mcts-icon" />
              <h3>Pure MCTS Algorithm</h3>
            </div>
            <p className="mcts-description">
              This agent uses Monte Carlo Tree Search without neural network guidance. Instead of learning patterns from training data, it explores the game tree through random playouts and statistical sampling.
            </p>

            <div className="mcts-steps">
              <div className="mcts-step step-1">
                <div className="step-title">1. Selection</div>
                <div className="step-text">Navigate the tree using UCB1 formula to balance exploration and exploitation</div>
              </div>

              <div className="mcts-step step-2">
                <div className="step-title">2. Expansion</div>
                <div className="step-text">Add new child nodes for unexplored moves</div>
              </div>

              <div className="mcts-step step-3">
                <div className="step-title">3. Simulation (Rollout)</div>
                <div className="step-text">Play random moves until game end to evaluate position</div>
              </div>

              <div className="mcts-step step-4">
                <div className="step-title">4. Backpropagation</div>
                <div className="step-text">Update win rates along the path back to root</div>
              </div>
            </div>

            <div className="mcts-difference">
              <div className="difference-title">Key Difference from AlphaZero:</div>
              <div className="difference-text">
                No neural network means no learned intuition. Each position is evaluated through simulation rather than pattern recognition, making the agent purely algorithmic.
              </div>
            </div>
          </div>
        </div>
      );
    }

    const arch = agent.architecture;

    return (
      <div className="network-viz">
        <div className="layer-row">
          <div className="layer-label">Input</div>
          <div className="layer-content">
            <div className="layer-box input-layer">
              <div className="layer-title">Conv1D Layer</div>
              <div className="layer-detail">
                {arch.gameSize} × {arch.inputChannels} → {arch.gameSize} × {arch.convChannels}, kernel=3
              </div>
              <div className="layer-note">Board state encoded with {arch.gameSize} positions</div>
            </div>
          </div>
        </div>

        <div className="layer-row">
          <div className="layer-label">Processing</div>
          <div className="layer-content">
            {Array.from({ length: arch.resBlocks }, (_, i) => (
              <div key={i} className="layer-box processing-layer">
                <div className="layer-title">ResBlock {i + 1}</div>
                <div className="layer-detail">{arch.convChannels} channels, 2 conv layers + skip connection</div>
              </div>
            ))}
          </div>
        </div>

        <div className="heads-row">
          <div className="head-column">
            <div className="head-label">Policy Head</div>
            <div className="layer-box policy-head">
              <div className="layer-title">Move Predictor</div>
              <div className="layer-detail">
                {arch.gameSize} × {arch.convChannels} → {arch.policyLayers.slice(0, -1).join(' → ')} → {arch.numMoves} moves
              </div>
              <div className="layer-note">Outputs probability distribution over legal moves</div>
            </div>
          </div>
          <div className="head-column">
            <div className="head-label">Value Head</div>
            <div className="layer-box value-head">
              <div className="layer-title">Position Evaluator</div>
              <div className="layer-detail">
                {arch.gameSize} × {arch.convChannels} → {arch.valueLayers.slice(0, -1).join(' → ')} → 1
              </div>
              <div className="layer-note">Estimates win probability from current position</div>
            </div>
          </div>
        </div>

        <div className="batchnorm-note">
          <div className="note-icon">ℹ️</div>
          <div className="note-text">All convolutional and fully-connected layers use BatchNorm1D for training stability</div>
                      <div className="note-icon">ℹ️</div>
          <div className="note-text">
              {parseInt(arch.gameSize) === 270 ? "AlphaQ Up was trained to compete on all seven graphs hosted here"  : "E is the number of edges in the game graph"}
          </div>
        </div>
      </div>
    );
  };

  const StatsPanel = () => (
    <div className="stats-grid">
      <div className="stat-card stat-rollouts">
        <div className="stat-header">
          <Zap className="stat-icon" />
          <div className="stat-label">MCTS Rollouts</div>
        </div>
        <div className="stat-value">{agent.mctsRollouts}</div>
        <div className="stat-subtitle">Simulations per move</div>
      </div>

      {agent.type === 'alphazero' && (
        <div className="stat-card stat-parameters">
          <div className="stat-header">
            <Brain className="stat-icon" />
            <div className="stat-label">Parameters</div>
          </div>
          <div className="stat-value">{agent.totalParameters.toLocaleString()}</div>
          <div className="stat-subtitle">Trainable weights</div>
        </div>
      )}

      {agent.type === 'alphazero' && (
        <div className="stat-card stat-architecture">
          <div className="stat-header">
            <Layers className="stat-icon" />
            <div className="stat-label">Architecture</div>
          </div>
          <div className="stat-value">{agent.architecture.resBlocks}</div>
          <div className="stat-subtitle">Residual blocks</div>
        </div>
      )}

      {agent.type === 'mcts' && (
        <div className="stat-card stat-algorithm">
          <div className="stat-header">
            <Network className="stat-icon" />
            <div className="stat-label">Algorithm</div>
          </div>
          <div className="stat-value-text">Pure MCTS</div>
          <div className="stat-subtitle">No neural network</div>
        </div>
      )}

      <div className="stat-card stat-difficulty">
        <div className="stat-header">
          <Shield className="stat-icon" />
          <div className="stat-label">Difficulty</div>
        </div>
        <div className="stat-value-text">{agent.difficulty}</div>
        <div className="stat-subtitle">Challenge level</div>
      </div>
    </div>
  );

  return (
    <div className="agent-profile-container">
      <div className="agent-profile-content">
        <div className="agent-header">
          <div className="header-gradient"></div>
          <div className="header-content">
            <div className="agent-icon-wrapper">
              <div className="icon-glow"></div>
              <img
                src={agent.iconUrl}
                alt={agent.name}
                className="agent-icon"
              />
            </div>
            <div className="agent-info">
              <div className="agent-title-row">
                <h1 className="agent-name">{agent.name}</h1>
                {agent.type !== 'other' && (
                  <span className={`agent-badge ${agent.type}`}>
                    {agent.type === 'alphazero' ? 'AlphaZero AI' : 'MCTS AI'}
                  </span>
                )}
              </div>
              <p className="agent-description">{agent.description}</p>
              <div className="agent-strengths">
                {agent.strengths.map((strength, i) => (
                  <span key={i} className="strength-tag">{strength}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {agent.type !== 'other' && (
          <>
            <div className="tab-navigation">
              <button
                onClick={() => setSelectedTab('overview')}
                className={`tab-button ${selectedTab === 'overview' ? 'active' : ''}`}
              >
                Overview
              </button>
              <button
                onClick={() => setSelectedTab('architecture')}
                className={`tab-button ${selectedTab === 'architecture' ? 'active' : ''}`}
              >
                {agent.type === 'alphazero' ? 'Neural Architecture' : 'Algorithm Details'}
              </button>
            </div>

            <div className="agent-content-section">
              {selectedTab === 'overview' && (
                <div className="overview-content">
                  <div className="stats-section">
                    <h2 className="section-title">
                      <Target className="title-icon" />
                      Agent Statistics
                    </h2>
                    <StatsPanel />
                  </div>

                  <div className="unique-section">
                    <h3 className="unique-title">What Makes This Agent Unique?</h3>
                    <div className="unique-content">
                      {agent.type === 'alphazero' ? (
                        <>
                          <p>
                            <strong>Compact Intelligence:</strong> With {agent.totalParameters.toLocaleString()} parameters, this agent achieves remarkable performance through efficient architecture design.
                          </p>
                          <p>
                            <strong>Dual-Head Design:</strong> Simultaneously predicts optimal moves (policy head) while evaluating board positions (value head) for superior decision-making.
                          </p>
                          <p>
                            <strong>MCTS Enhancement:</strong> Each move is backed by {agent.mctsRollouts} Monte Carlo Tree Search simulations, blending neural intuition with strategic lookahead.
                          </p>
                        </>
                      ) : (
                        <>
                          <p>
                            <strong>Pure Algorithm:</strong> No neural network dependencies means completely transparent decision-making through Monte Carlo Tree Search.
                          </p>
                          <p>
                            <strong>Statistical Evaluation:</strong> Positions are evaluated through {agent.mctsRollouts} random playouts, providing robust move selection through statistical sampling.
                          </p>
                          <p>
                            <strong>Exploration Balance:</strong> Uses UCB1 (Upper Confidence Bound) formula to optimally balance exploring new moves versus exploiting known good positions.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'architecture' && (
                <div className="architecture-content">
                  <div className="architecture-header">
                    <h2 className="section-title">
                      {agent.type === 'alphazero' ? (
                        <><Brain className="title-icon" />Neural Network Architecture</>
                      ) : (
                        <><GitBranch className="title-icon" />MCTS Algorithm</>
                      )}
                    </h2>
                    <p className="architecture-subtitle">
                      {agent.type === 'alphazero'
                        ? "A deep dive into the AI brain powering this agent's decision-making"
                        : "How pure Monte Carlo Tree Search explores the game tree"}
                    </p>
                  </div>

                  <NetworkVisualization />

                  {agent.type === 'alphazero' && (
                    <div className="insights-section">
                      <h3 className="insights-title">Architecture Insights</h3>
                      <div className="insights-grid">
                        <div className="insight-item">
                          <div className="insight-label">Convolutional Backbone</div>
                          <div className="insight-text">Uses 1D convolutions to extract spatial patterns from the game state</div>
                        </div>
                        <div className="insight-item">
                          <div className="insight-label">Residual Learning</div>
                          <div className="insight-text">Skip connections allow deeper networks without vanishing gradients</div>
                        </div>
                        <div className="insight-item">
                          <div className="insight-label">Policy-Value Split</div>
                          <div className="insight-text">Separate heads optimize for move selection and position evaluation</div>
                        </div>
                        <div className="insight-item">
                          <div className="insight-label">Batch Normalization</div>
                          <div className="insight-text">Stabilizes training and improves convergence across all layers</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/*<div className="action-section">*/}
        {/*  <button className="challenge-button">*/}
        {/*    Challenge {agent.name}*/}
        {/*  </button>*/}
        {/*</div>*/}
      </div>
    </div>
  );
};

export default AgentProfile;